import { createContext, useContext, useState, useCallback, useRef, type ReactNode } from "react";

interface MastermindContextType {
  content: string;
  setContent: (content: string) => void;
  selectedTool: string | null;
  setSelectedTool: (tool: string | null) => void;
  isCollapsed: boolean;
  setIsCollapsed: (collapsed: boolean | ((prev: boolean) => boolean)) => void;
  activeTab: string;
  setActiveTab: (tab: string) => void;
  currentSpaceId: string | null;
  setCurrentSpaceId: (spaceId: string | null) => void;
  currentTopicId: string | null;
  setCurrentTopicId: (topicId: string | null) => void;
  currentThreadId: string | null;
  setCurrentThreadId: (threadId: string | null) => void;
  addClip: (text: string) => void;
  clipToSidebar: (content: string, title?: string, documentType?: string) => void;
  registerSidebarClipHandler: (handler: (content: string, title?: string, documentType?: string) => void) => void;
  cancelDocumentEdit: () => void;
  registerCancelEditHandler: (handler: () => void) => void;
}

const MastermindContext = createContext<MastermindContextType | undefined>(undefined);

const STORAGE_KEY_RIGHT_PANEL_COLLAPSED = 'rightPanelCollapsed';

export function MastermindProvider({ children }: { children: ReactNode }) {
  const [content, setContent] = useState("");
  const [selectedTool, setSelectedTool] = useState<string | null>(null);
  const [isCollapsedState, setIsCollapsedState] = useState<boolean>(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem(STORAGE_KEY_RIGHT_PANEL_COLLAPSED);
      if (saved !== null) {
        return saved === 'true';
      }
      return true;
    }
    return true;
  });
  const [activeTab, setActiveTab] = useState("knowledge");
  const [currentSpaceId, setCurrentSpaceId] = useState<string | null>(null);
  const [currentTopicId, setCurrentTopicId] = useState<string | null>(null);
  // Always start with no thread so app open shows a clean new chat (no restore from localStorage)
  const [currentThreadId, setCurrentThreadIdState] = useState<string | null>(null);
  const [sidebarClipHandler, setSidebarClipHandler] = useState<((content: string, title?: string, documentType?: string) => void) | null>(null);
  const cancelEditHandlerRef = useRef<(() => void) | null>(null);

  // Wrapper to sync collapsed state with localStorage (supports functional updates like React setState)
  const setIsCollapsed = useCallback((collapsed: boolean | ((prev: boolean) => boolean)) => {
    setIsCollapsedState((prev) => {
      const next = typeof collapsed === "function" ? collapsed(prev) : collapsed;
      if (typeof window !== "undefined") {
        localStorage.setItem(STORAGE_KEY_RIGHT_PANEL_COLLAPSED, String(next));
      }
      return next;
    });
  }, []);

  // Wrapper to sync with localStorage
  const setCurrentThreadId = useCallback((threadId: string | null) => {
    setCurrentThreadIdState(threadId);
    if (typeof window !== 'undefined') {
      if (threadId) {
        localStorage.setItem('currentChatThreadId', threadId);
      } else {
        localStorage.removeItem('currentChatThreadId');
      }
    }
  }, []);

  const addClip = useCallback((text: string) => {
    // Add text to content, auto-select clipping tool, expand sidebar, switch to knowledge tab
    setContent((prev) => (prev ? `${prev}\n\n---\n\n${text}` : text));
    setSelectedTool("clipping");
    setIsCollapsed(false);
    setActiveTab("knowledge");
  }, [setIsCollapsed]);

  const clipToSidebar = useCallback((content: string, title?: string, documentType?: string) => {
    if (sidebarClipHandler) {
      sidebarClipHandler(content, title, documentType);
      // Avoid a Mastermind context update (and broad re-renders) when the panel is already open
      setIsCollapsed((prev) => (prev ? false : prev));
    }
  }, [sidebarClipHandler, setIsCollapsed]);

  const registerSidebarClipHandler = useCallback((handler: (content: string, title?: string, documentType?: string) => void) => {
    setSidebarClipHandler(() => handler);
  }, []);

  const cancelDocumentEdit = useCallback(() => {
    cancelEditHandlerRef.current?.();
  }, []);

  const registerCancelEditHandler = useCallback((handler: () => void) => {
    cancelEditHandlerRef.current = handler;
  }, []);

  return (
    <MastermindContext.Provider
      value={{
        content,
        setContent,
        selectedTool,
        setSelectedTool,
        isCollapsed: isCollapsedState,
        setIsCollapsed,
        activeTab,
        setActiveTab,
        currentSpaceId,
        setCurrentSpaceId,
        currentTopicId,
        setCurrentTopicId,
        currentThreadId,
        setCurrentThreadId,
        addClip,
        clipToSidebar,
        registerSidebarClipHandler,
        cancelDocumentEdit,
        registerCancelEditHandler,
      }}
    >
      {children}
    </MastermindContext.Provider>
  );
}

export function useMastermind() {
  const context = useContext(MastermindContext);
  if (!context) {
    throw new Error("useMastermind must be used within a MastermindProvider");
  }
  return context;
}
